import { Component, inject, OnInit, signal } from '@angular/core';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { map, Observable, switchMap } from 'rxjs';
import {
  urlValidator,
  commercialRegisterNumberValidator
} from 'src/app/services/form/form-validators';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  TextFieldComponent,
  TextareaFieldComponent,
  DatepickerFieldComponent,
  FormSectionComponent,
  ButtonComponent,
  TextFieldWithErrorComponent,
  DatepickerFieldWithErrorComponent,
  TextareaFieldWithErrorComponent,
  DropdownFieldComponent,
  DropdownOption,
  AlertComponent
} from 'htx-ui-library';

import {
  OrganizationDetails,
  OrganizationUpdateDto,
  ActivityState
} from '../../../../../services/organizations/organization.model';
import { OrganizationService } from '../../../../../services/organizations/organization.service';
import { NavigationService } from '../../../../../services/navigation.service';
import { ProtectedContentComponent } from '../../../../organisms/protected-content/protected-content.component';
import { AuthService } from '../../../../../services/auth/auth.service';
import { UserRole } from '../../../../../services/auth/auth.role';

@Component({
  selector: 'app-organization-edit-page',
  imports: [
    TranslocoDirective,
    TranslocoPipe,
    ReactiveFormsModule,
    FormSectionComponent,
    ButtonComponent,
    TextFieldWithErrorComponent,
    DatepickerFieldWithErrorComponent,
    TextareaFieldWithErrorComponent,
    ProtectedContentComponent,
    DropdownFieldComponent,
    AlertComponent
  ],
  templateUrl: './organization-edit-page.component.html',
  styleUrl: './organization-edit-page.component.scss'
})
export class OrganizationEditPageComponent implements OnInit {
  private fb: FormBuilder = inject(FormBuilder);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private organizationService = inject(OrganizationService);
  private translocoService = inject(TranslocoService);
  private navigationService = inject(NavigationService);
  private authService = inject(AuthService);

  requiredRoles = this.navigationService.getAccessRoles('admin/organizations/edit');

  /** True if the user is a Data Space Authority */
  isDataspaceAuthority = this.authService.userRoles$.value.includes(UserRole.DataspaceAuthority);

  /** True if the user is an OrgAdmin or OrgRepresentative */
  isOrgEditor =
    this.authService.userRoles$.value.includes(UserRole.OrganizationAdmin) ||
    this.authService.userRoles$.value.includes(UserRole.OrganizationRepresentative);

  /** Sensitive fields that OrgAdmins cannot edit (only DSA can) */
  private readonly sensitiveFields = [
    'legalName', 'shortName', 'legalForm',
    'street', 'houseNumber', 'postalCode', 'city', 'country',
    'commercialRegisterNumber', 'commercialRegisterCourt', 'taxIdNumber', 'foundingDate'
  ] as const;

  /** DID warning visibility */
  showDidWarning = signal(false);

  /** Dropdown options for Activity Status */
  statusOptions = signal<DropdownOption[]>([]);

  fieldLabels: { [key: string]: string } = {};

  id$ = this.activatedRoute.paramMap.pipe(map((data) => data.get('id')!));

  data$: Observable<OrganizationDetails> = this.id$.pipe(
    switchMap((id) => this.organizationService.getDetailsById(id))
  );
  form = this.fb.group({
    legalName: ['', [Validators.required, Validators.maxLength(100)]],
    shortName: ['', [Validators.required, Validators.maxLength(20)]],
    legalForm: ['', Validators.required],
    sector: ['', [Validators.required, Validators.maxLength(200)]],
    website: ['', [urlValidator, Validators.maxLength(150)]],
    street: ['', [Validators.required, Validators.maxLength(56)]],
    houseNumber: ['', [Validators.required, Validators.maxLength(5)]],
    postalCode: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(8)]],
    city: ['', [Validators.required, Validators.maxLength(40)]],
    country: ['', [Validators.required, Validators.maxLength(40)]],
    description: ['', [Validators.required, Validators.maxLength(2000)]],
    commercialRegisterNumber: ['', [Validators.required, Validators.maxLength(2000)]],
    commercialRegisterCourt: ['', [Validators.required, Validators.maxLength(2000)]],
    taxIdNumber: ['', [Validators.required, Validators.maxLength(2000)]],
    foundingDate: [new Date(), [Validators.required]],
    did: [''],
    activityState: ['']
  });

  ngOnInit(): void {
    // Load field labels from translations
    const controlNames = [
      'legalName',
      'shortName',
      'legalForm',
      'sector',
      'website',
      'street',
      'houseNumber',
      'postalCode',
      'city',
      'commercialRegisterNumber',
      'commercialRegisterCourt',
      'taxIdNumber',
      'foundingDate',
      'description'
    ];

    controlNames.forEach((controlName) => {
      this.fieldLabels[controlName] = this.translocoService.translate(
        `organization.edit.${controlName}.label`
      );
    });

    // Initialize status dropdown options
    this.statusOptions.set([
      {
        value: ActivityState.ACTIVE,
        label: this.translocoService.translate('organization.control.activityState.options.ACTIVE')
      },
      {
        value: ActivityState.INACTIVE,
        label: this.translocoService.translate(
          'organization.control.activityState.options.INACTIVE'
        )
      }
    ]);

    this.data$.subscribe((data) => {
      this.form.patchValue({
        legalName: data.legalName,
        shortName: data.shortName,
        legalForm: data.legalForm,
        sector: data.sector,
        website: data.website,
        street: data.organisationAddress.street,
        houseNumber: data.organisationAddress.houseNumber,
        postalCode: data.organisationAddress.postalCode,
        city: data.organisationAddress.city,
        country: data.organisationAddress.country,
        description: data.description,
        commercialRegisterNumber: data.commercialRegisterNumber,
        commercialRegisterCourt: data.commercialRegisterCourt,
        taxIdNumber: data.taxIdNumber,
        foundingDate: data.foundingDate ? new Date(data.foundingDate) : null,
        did: data.did || '',
        activityState: data.activityState || ''
      });

      // Disable sensitive fields for OrgAdmin/OrgRepresentative (only DSA can edit these)
      if (this.isOrgEditor && !this.isDataspaceAuthority) {
        this.sensitiveFields.forEach(field => {
          this.form.get(field)?.disable();
        });
      }
    });

    // Show DID warning when the DID field value changes
    this.form.controls.did.valueChanges.subscribe(() => {
      this.showDidWarning.set(true);
    });
  }

  backToDetail() {
    this.id$.subscribe((id) => {
      void this.router.navigate(['admin', 'organizations', 'details', id]);
    });
  }

  save() {
    this.id$
      .pipe(
        switchMap((id) => {
          const formValues = this.form.getRawValue();
          const rawValue: OrganizationUpdateDto = {
            id: id,
            ...formValues,
            activityState: (formValues.activityState as ActivityState) || undefined
          };
          console.log(rawValue);
          return this.organizationService.update(id, rawValue);
        })
      )
      .subscribe((result) => {
        void this.router.navigate(['admin', 'organizations', 'details', result]);
      });
  }
}
